import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { entry } from '@/routes';
import { signInWithEmail } from '@/lib/platform/auth';
import { enterAfterAuth } from '@/lib/platform/routing';

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handleSignIn = async () => {
    setMessage(null);
    setIsPending(true);

    const result = await signInWithEmail(email, password);
    if (result.ok) {
      await enterAfterAuth(entry);
      return;
    }

    setMessage(result.message);
    setIsPending(false);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12 font-sans text-foreground">
      <div className="w-full max-w-md">
        <header className="mb-8 space-y-3">
          <p className="text-sm font-semibold tracking-[0.18em] text-accent-foreground uppercase">TrustFile</p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Sign in</h1>
          <p className="text-sm leading-6 text-muted-foreground">Secure access to your tax filing workspace.</p>
        </header>

        <div className="space-y-8">
          <section aria-labelledby="sign-in-form-heading">
            <h2 id="sign-in-form-heading" className="sr-only">
              Sign-in form
            </h2>
            <div className="rounded-[0.375rem] border border-border bg-card p-6 shadow-sm">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="sign-in-email">Email</Label>
                  <Input
                    id="sign-in-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="h-11 rounded-[0.375rem]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sign-in-password">Password</Label>
                  <Input
                    id="sign-in-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-11 rounded-[0.375rem]"
                  />
                </div>
                {message ? (
                  <p role="alert" className="text-sm leading-5 text-destructive">
                    {message}
                  </p>
                ) : null}
                <Button
                  type="button"
                  data-jr-key="sign-in-submit"
                  disabled={isPending}
                  onClick={() => void handleSignIn()}
                  className="h-11 w-full rounded-[0.375rem] bg-primary text-primary-foreground hover:bg-primary/85 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isPending ? 'Signing in…' : 'Sign in'}
                </Button>
              </div>
            </div>
          </section>

          <section aria-labelledby="sign-in-help-heading" className="space-y-4">
            <h2 id="sign-in-help-heading" className="text-sm font-semibold text-foreground">
              Need access?
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Use the email and password associated with your TrustFile account. You can create an account if you do not
              have one yet.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
              <Link
                to="/sign-up"
                className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-[0.375rem] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Create an account
              </Link>
              <Link
                to="/"
                className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-[0.375rem] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Return to TrustFile
              </Link>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
